/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import { ResponsiveTable } from "../components/mobile/ResponsiveTable";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { toast } from "../components/ui/sonner";
import * as XLSX from "xlsx";
import { HardHat, Wallet, AlertTriangle, Search, Landmark, FileSpreadsheet, Banknote, Upload, History, Loader2 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, LabelList } from "recharts";
import { branchColor } from "./ProjectFinanceDetail";
import CollectionsWorkbench from "../components/CollectionsWorkbench";
import { FinExcelImportDialog } from "../components/finance/FinExcelImportDialog";
import { ink } from "../lib/ink";
import { CardSkeleton, TableSkeleton, EmptyState } from "../components/States";
import ReceivablePopover from "../components/finance/ReceivablePopover";
import CollectionsChartsPanel from "../components/finance/CollectionsChartsPanel";
import { CollectionPolicyStatus } from "../components/crm/CollectionPolicyStatus";

const money = (n) => "$" + Math.round(Number(n || 0)).toLocaleString();
const HEALTH = {
  risk: { zh: "风险", en: "Risk", cls: "bg-red-100 text-red-700" },
  watch: { zh: "关注", en: "Watch", cls: "bg-amber-100 text-amber-700" },
  good: { zh: "健康", en: "Good", cls: "bg-green-100 text-green-700" },
  settled: { zh: "已结算", en: "Settled", cls: "bg-slate-100 text-slate-500" },
};

/* 服务端分页每页条数 */
const PAGE_SIZE = 20;

/**
 * ProjectFinanceList —— 项目财务页。
 *
 * 展示需求（2026-10-21）：取消进入页面自动弹出的财务报表，将分公司汇总、KPI、
 * 盈利对比和收款图表常驻页面顶部，随后显示催收工作台和项目列表。
 * 统计直接复用本页的数据与筛选状态，避免迁移展示位置后产生重复请求或统计口径变化。
 *
 * 需求变更（2026-10-13）：把原来两块列表 ——「应收项目 · 与施工进度同步」（逐期次可开单/收款）
 * 与「所有项目列表」（财务总览）—— 合并为「所有项目列表」一张表，并新增「收款状态」列。
 * 收款状态由 payments 现算（待开单 ×N · 已开票 ×M / 已收清），点击弹出浮窗逐期次操作。
 *
 * 数据策略：**服务端分页 + 分页器**（每页 20 条，走 `limit`/`offset`），筛选条件全部下传后端
 * （含 payState 收款状态筛选 —— 后端 `_pfl_filter` 按每行的 payPending/payInvoiced 过滤）。
 * 每页响应都带全量 meta（facets / branchGroups / branchOf / chart / totals / summary），
 * 所以分公司分组、筛选下拉、盈利对比图不受分页影响，始终是全量口径。
 */
export default function ProjectFinanceList() {
  const { api, lang, user, visibleProjects } = useApp();
  const zh = lang === "zh";
  const nav = useNavigate();
  // 筛选条件：分公司 / 销售 / 设计师 / 小组 / 阶段 / 健康度 / 收款状态 / 关键字（全部下传后端筛选）
  const [f, setF] = useState({ branch: "", sales: "", designer: "", team: "", phase: "", health: "", payState: "", q: "" });
  const [qd, setQd] = useState("");
  useEffect(() => { const t = setTimeout(() => setQd(f.q.trim()), 300); return () => clearTimeout(t); }, [f.q]);

  const canManage = ["admin", "finance", "ceo"].includes(user?.role);
  const [autoCollect, setAutoCollect] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [exporting, setExporting] = useState(false);
  useEffect(() => {
    if (["admin", "finance", "ceo"].includes(user?.role))
      api.get("/settings/collection-sms-auto").then(({ data }) => setAutoCollect(data)).catch(() => {});
  }, [api, user]);

  // ---- 服务端分页取数 ----
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  // 请求参数：limit/offset + 非空筛选条件（含 payState）
  const params = useMemo(() => Object.fromEntries(Object.entries({
    limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE,
    branch: f.branch, sales: f.sales, designer: f.designer, team: f.team, phase: f.phase, health: f.health, payState: f.payState, q: qd,
  }).filter(([, v]) => v !== "" && v != null)), [page, f.branch, f.sales, f.designer, f.team, f.phase, f.health, f.payState, qd]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/finance/projects-overview", { params })
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => { if (alive) toast.error(e?.response?.data?.detail || e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api, params, tick]);

  // 任一筛选变化 → 回到第 1 页
  useEffect(() => { setPage(1); }, [f.branch, f.sales, f.designer, f.team, f.phase, f.health, f.payState, qd]);

  // id → 项目（含 payments）：浮窗逐期次明细从这里取（visibleProjects 为当前用户可见的全量项目）
  const payInfo = useMemo(() => {
    const m = {};
    for (const p of visibleProjects || []) m[p.id] = p;
    return m;
  }, [visibleProjects]);

  const list = data?.rows || [];
  const summary = data?.summary || {};
  const threshold = Number(data?.threshold || 0);
  const total = data?.total || 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const branchOf = data?.branchOf || {};
  const branchGroups = data?.branchGroups || [];

  // 报表图表用的项目集：与表格同一个分公司筛选口径（点分公司 chip → 报表同步筛）
  const scopedProjects = useMemo(() => {
    if (!f.branch) return visibleProjects || [];
    return (visibleProjects || []).filter((p) => branchOf[p.id] === f.branch);
  }, [visibleProjects, f.branch, branchOf]);
  const totalCollected = data?.totals?.collected || 0;
  const totalOther = data?.totals?.otherCollected || 0;
  const opts = data?.facets || { branch: [], sales: [], designer: [], team: [], phase: [] };
  const allCount = summary.count ?? 0;
  const openCount = summary.openCount ?? 0;

  // 分页器页码窗口：当前页 ±2，最多 5 个
  const pageNums = useMemo(() => {
    const w = 2;
    let s = Math.max(1, page - w), e = Math.min(pageCount, page + w);
    if (e - s < 4) { if (s === 1) e = Math.min(pageCount, s + 4); else s = Math.max(1, e - 4); }
    const arr = [];
    for (let i = s; i <= e; i += 1) arr.push(i);
    return arr;
  }, [page, pageCount]);

  const s = summary;
  const th = threshold;
  const marginCls = (m) => (m == null ? "bg-slate-100 text-slate-400" : m < th ? "bg-red-100 text-red-700" : m < th + 10 ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700");
  const sel = "text-sm rounded-md border border-slate-200 px-2.5 py-1.5 bg-white";
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const HEALTH_LBL = (h) => (zh ? (HEALTH[h] || HEALTH.good).zh : (HEALTH[h] || HEALTH.good).en);
  const exportXlsx = async () => {
    setExporting(true);
    try {
      // 导出当前筛选结果：不带 limit/offset 拉全部筛选行（含收款状态筛选）
      const exportParams = Object.fromEntries(Object.entries(params).filter(([k, v]) => v !== "" && v != null && k !== "limit" && k !== "offset"));
      const { data: ex } = await api.get("/finance/projects-overview", { params: exportParams });
      const all = ex.rows || [];
      const ws = XLSX.utils.json_to_sheet(all.map((r) => ({
        [zh ? "项目号" : "Project No."]: r.designNo || r.id, [zh ? "项目" : "Project"]: r.address, [zh ? "客户" : "Client"]: r.clientName,
        [zh ? "记录类型" : "Record type"]: r.source === "signed_customer" ? (zh ? "已签客户" : "Signed customer") : (zh ? "工程项目" : "Construction project"),
        [zh ? "分公司" : "Branch"]: r.branchLabel, [zh ? "销售" : "Sales"]: r.sales,
        [zh ? "设计师" : "Designer"]: r.designer, [zh ? "项目组" : "Team"]: r.teamName,
        [zh ? "项目阶段" : "Phase"]: r.source === "signed_customer" ? (zh ? "设计阶段" : "Design") : r.phase,
        [zh ? "合同额" : "Contract"]: r.contract, [zh ? "已收" : "Collected"]: r.collected,
        [zh ? "应收AR" : "AR"]: r.ar, [zh ? "账龄(天)" : "AR Days"]: r.arDays || 0,
        [zh ? "应付AP" : "AP"]: r.ap, [zh ? "成本" : "Cost"]: r.cost,
        [zh ? "毛利" : "Profit"]: r.profit, [zh ? "毛利率%" : "Margin%"]: r.margin,
        [zh ? "健康度" : "Health"]: HEALTH_LBL(r.health),
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "ProjectFinance");
      const parts = [f.branch, f.sales, f.designer, f.team, f.phase, f.health && HEALTH_LBL(f.health), qd].filter(Boolean);
      XLSX.writeFile(wb, `项目财务${parts.length ? "_" + parts.join("_") : ""}_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(zh ? `已导出 ${all.length} 个项目${parts.length ? "（含当前筛选条件）" : ""}` : `Exported ${all.length} projects`);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setExporting(false);
  };
  const arDaysCls = (dys) => (dys >= 30 ? "text-red-700 font-bold" : dys >= 15 ? "text-red-500 font-semibold" : "text-amber-600");

  if (loading && !data) return <div className="space-y-5" data-testid="pfl-loading"><CardSkeleton count={6} className="grid-cols-2 md:grid-cols-3 xl:grid-cols-6" /><div className="bg-white rounded-xl border border-slate-200"><TableSkeleton rows={8} cols={7} /></div></div>;

  return (
    <div className="space-y-5" data-testid="project-finance-list">
      <div>
        {/* 页面唯一 h1：按全站「标题去重」约定，.app-page-content 下的 h1 会被 index.css 置为 sr-only，
            仅服务屏幕阅读器 / 文档结构，不占视觉空间。原副标题文案已按需求移除（2026-10-14）。 */}
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><HardHat className="h-6 w-6 text-red-600" />{zh ? "项目财务" : "Project Finance"}</h1>
      </div>

      {/* 报表改为页面顶部常驻区，随整页滚动；保留统计 testid 以及分公司与下方列表的联动。 */}
      <section className="min-w-0 space-y-5" aria-label={zh ? "财务统计" : "Finance statistics"} data-testid="pfl-report">
        {/* branch summary strip */}
        <div className="flex flex-wrap gap-2" data-testid="pfl-branch-strip">
          <button data-testid="pfl-branch-chip-all" onClick={() => setF((p) => ({ ...p, branch: "" }))}
            className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 rounded-xl border px-4 py-2.5 text-left transition-colors ${!f.branch ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}>
            <p className="text-[11px] font-medium opacity-80">{zh ? "全部分公司" : "All branches"}</p>
            <p className="text-sm font-bold mt-0.5">{allCount} {zh ? "项目" : "projects"} · AR {money(s.totalAr)}</p>
          </button>
          {branchGroups.map((b) => {
            const active = f.branch === b.label;
            const clr = branchColor(b.key, b.label);
            return (
              <button key={b.label} data-testid={`pfl-branch-chip-${b.label}`} onClick={() => setF((p) => ({ ...p, branch: active ? "" : b.label }))}
                className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 rounded-xl border px-4 py-2.5 text-left transition-colors ${active ? "text-white" : "bg-white hover:opacity-90"}`}
                style={active ? { background: clr, borderColor: clr } : { borderColor: clr + "66" }}>
                <p className="text-[12px] font-bold flex items-center gap-1.5" style={active ? {} : { color: clr }}>
                  <span className="w-2 h-2 rounded-full inline-block" style={{ background: active ? "#fff" : clr }} />{b.label} · {b.count} {zh ? "项目" : ""}
                </p>
                <p className={`text-[11px] mt-0.5 ${active ? "text-white/90" : "text-slate-500"}`}>
                  {zh ? "应收" : "AR"} <span className="font-semibold">{money(b.ar)}</span> · {zh ? "工程毛利" : "Construction profit"} <span className={`font-semibold ${!active && b.profit < 0 ? "text-red-600" : ""}`}>{money(b.profit)}</span>
                </p>
              </button>
            );
          })}
        </div>

        {/* 页面正文比原浮窗窄，中等桌面按三列展示，避免侧栏挤压金额；宽桌面保持六列。 */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="pfl-kpis">
          <Kpi tid="pfl-kpi-count" label={zh ? "签约项目" : "Projects"} value={allCount} icon={HardHat} color="#1e293b" />
          <Kpi tid="pfl-kpi-collected" label={zh ? "合同已收合计" : "Contract collected"} value={money(totalCollected)} sub={totalOther > 0 ? (zh ? `其他收款 +${money(totalOther)} · 总到账 ${money(totalCollected + totalOther)}` : `other +${money(totalOther)} · total ${money(totalCollected + totalOther)}`) : null} icon={Banknote} color="#15803d" />
          <Kpi tid="pfl-kpi-ar" label={zh ? "应收合计 AR" : "Total AR"} value={money(s.totalAr)} icon={Wallet} color="#1d4ed8" />
          <Kpi tid="pfl-kpi-ap" label={zh ? "应付合计 AP" : "Total AP"} value={money(s.totalAp)} icon={Landmark} color="#b45309" />
          <Kpi tid="pfl-kpi-risk" label={zh ? "风险项目" : "At Risk"} value={s.risk ?? 0} icon={AlertTriangle} color="#b91c1c" />
          <Kpi tid="pfl-kpi-watch" label={zh ? "需关注" : "Watch"} value={s.watch ?? 0} icon={AlertTriangle} color="#b45309" />
        </div>

        {/* project comparison chart (drill-down) */}
        <CompareChart chart={data?.chart} zh={zh} nav={nav} api={api} />

        {/* 收款报表 3 图 */}
        <CollectionsChartsPanel projects={scopedProjects} />
      </section>

      {/* collections workbench (merged from 财务收款) */}
      <CollectionsWorkbench branchFilter={f.branch} branchOf={branchOf} />

      {/* 「所有项目列表」标题行已按需求移除（2026-10-14）：标题与「点击项目进入财务详情」说明不再显示 */}

      <div className="flex flex-wrap items-center gap-2" data-testid="pfl-filters">
        <span className="relative">
          <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input data-testid="pfl-search" value={f.q} onChange={set("q")} placeholder={zh ? "搜项目号/地址/客户/销售…" : "Search project no./address/client/sales…"}
            className="text-sm rounded-md border border-slate-200 pl-8 pr-2.5 py-1.5 w-44 bg-white" />
        </span>
        <select data-testid="pfl-filter-branch" className={sel} value={f.branch} onChange={set("branch")}>
          <option value="">{zh ? "全部分公司" : "All branches"}</option>
          {opts.branch.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select data-testid="pfl-filter-sales" className={sel} value={f.sales} onChange={set("sales")}>
          <option value="">{zh ? "全部销售" : "All sales"}</option>
          {opts.sales.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select data-testid="pfl-filter-designer" className={sel} value={f.designer} onChange={set("designer")}>
          <option value="">{zh ? "全部设计师" : "All designers"}</option>
          {opts.designer.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select data-testid="pfl-filter-team" className={sel} value={f.team} onChange={set("team")}>
          <option value="">{zh ? "全部项目组" : "All teams"}</option>
          {opts.team.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select data-testid="pfl-filter-phase" className={sel} value={f.phase} onChange={set("phase")}>
          <option value="">{zh ? "全部阶段" : "All phases"}</option>
          {opts.phase.map((v) => <option key={v} value={v}>{v === "design" ? (zh ? "设计阶段" : "Design") : v}</option>)}
        </select>
        <select data-testid="pfl-filter-health" className={sel} value={f.health} onChange={set("health")}>
          <option value="">{zh ? "全部健康度" : "All health"}</option>
          <option value="risk">{zh ? "风险" : "Risk"}</option>
          <option value="watch">{zh ? "关注" : "Watch"}</option>
          <option value="good">{zh ? "健康" : "Good"}</option>
          <option value="settled">{zh ? "已结算" : "Settled"}</option>
        </select>
        {/* 收款状态筛选：来自 payments（前端现算），"只看应收"= 有未结款项（待开单/已开票） */}
        <select data-testid="pfl-filter-paystate" className={sel} value={f.payState} onChange={set("payState")}>
          <option value="">{zh ? "全部收款状态" : "All collection states"}</option>
          <option value="open">{zh ? "有应收（待开单/已开票）" : "Open receivables"}</option>
          <option value="pending">{zh ? "待开单" : "Awaiting invoice"}</option>
          <option value="invoiced">{zh ? "已开票" : "Invoiced"}</option>
          <option value="clear">{zh ? "已收清" : "Cleared"}</option>
        </select>
        <button type="button" data-testid="pfl-filter-open"
          onClick={() => setF((p) => ({ ...p, payState: p.payState === "open" ? "" : "open" }))}
          title={zh ? "只看有未结款项的项目（待开单 / 已开票）" : "Only projects with open receivables"}
          className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border transition-colors ${f.payState === "open" ? "border-brand-500 bg-brand-50 text-brand-700" : "border-slate-300 bg-white text-slate-600 hover:border-brand-400"}`}>
          <Wallet className="h-3 w-3" />{zh ? "只看应收" : "Open only"} · {openCount}
        </button>
        <span className="text-xs text-slate-400 ml-auto whitespace-nowrap" data-testid="pfl-count" data-loaded={list.length} data-total={total}>{zh ? `${list.length} / ${total} · 共 ${allCount}` : `${list.length} / ${total} · ${allCount} total`}</span>
        {canManage && autoCollect !== null && (
          <div className="w-full" data-testid="pfl-auto-collect-toggle" data-readonly="1"><CollectionPolicyStatus zh={zh} enabled={autoCollect.workflowEnabled !== false} intervalMinutes={autoCollect.intervalMinutes || 5} /></div>
        )}
        {canManage && (
          <button data-testid="pfl-import-btn" onClick={() => setShowImport(true)}
            className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border border-slate-300 text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors bg-white">
            <Upload className="h-3.5 w-3.5" />{zh ? "导入 Excel" : "Import"}
          </button>
        )}
        {["admin", "finance", "ceo", "sales_vp", "eng_manager"].includes(user?.role) && (
          <button data-testid="pfl-imports-link" onClick={() => nav("/finance/imports")} title={zh ? "手机也可查看：批次核对单 / 影响摘要 / QB 对照" : "Import history"}
            className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border border-slate-300 text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors bg-white">
            <History className="h-3.5 w-3.5" />{zh ? "导入历史" : "Imports"}
          </button>
        )}
        <button data-testid="pfl-export-btn" onClick={exportXlsx} disabled={exporting}
          className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border border-slate-300 text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors bg-white disabled:opacity-50">
          {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}{zh ? "导出 Excel" : "Export"}
        </button>
      </div>
      {showImport && <FinExcelImportDialog api={api} zh={zh} onClose={() => setShowImport(false)} onImported={reload} />}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="pfl-table">
        <div className="overflow-x-auto">
          <ResponsiveTable mobile="scroll" className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left font-medium px-4 py-3">{zh ? "项目/客户" : "Project"}</th>
                <th className="text-left font-medium px-3 py-3">{zh ? "分公司" : "Branch"}</th>
                <th className="text-left font-medium px-3 py-3">{zh ? "销售/小组" : "Sales/Team"}</th>
                <th className="text-left font-medium px-3 py-3">{zh ? "阶段" : "Phase"}</th>
                <th className="text-right font-medium px-3 py-3">{zh ? "合同额" : "Contract"}</th>
                <th className="text-right font-medium px-3 py-3">{zh ? "已收" : "Collected"}</th>
                <th className="text-right font-medium px-3 py-3">{zh ? "应收 AR" : "AR"}</th>
                <th className="text-right font-medium px-3 py-3">{zh ? "账龄" : "AR Days"}</th>
                <th className="text-left font-medium px-3 py-3">{zh ? "收款状态" : "Collections"}</th>
                <th className="text-right font-medium px-3 py-3">{zh ? "应付 AP" : "AP"}</th>
                <th className="text-right font-medium px-3 py-3">{zh ? "毛利率" : "Margin"}</th>
                <th className="text-center font-medium px-4 py-3">{zh ? "健康度" : "Health"}</th>
              </tr>
            </thead>
            <tbody>
              {loading && list.length === 0 && <tr><td colSpan={12} className="p-0"><TableSkeleton rows={6} cols={7} /></td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={12}><EmptyState compact icon={HardHat} title={zh ? "没有符合条件的项目" : "No projects match"} hint={zh ? "试试放宽分公司 / 阶段筛选" : "Try widening the branch / stage filters"} testId="pfl-empty" /></td></tr>}
              {list.map((r) => {
                const h = HEALTH[r.health] || HEALTH.good;
                return (
                  <tr key={r.id} data-testid={`pfl-row-${r.id}`} className={`border-t border-slate-100 hover:bg-slate-50/60 ${r.health === "risk" ? "bg-red-50/40" : ""}`}>
                    <td className="px-4 py-3">
                      <button data-testid={`pfl-addr-${r.id}`} onClick={() => nav(r.source === "signed_customer" ? `/crm/signed/${r.leadId}` : `/finance/project/${r.id}`)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-left group">
                        <p className="font-medium max-w-[220px] truncate group-hover:underline" style={ink(branchColor(r.branch, r.branchLabel))}>{r.address}</p>
                        {/* 副行口径对齐旧「应收项目」列表：客户（空则回退 manager） · 项目类型（默认 ADU，overview 不返回，从 visibleProjects 取） · 设计师 */}
                        <span className="text-[11px] text-slate-400">{(r.clientName || r.manager || "-")}{r.designNo ? ` · ${r.designNo}` : ""}{r.source === "signed_customer" ? ` · ${zh ? "已签客户" : "Signed customer"}` : (payInfo[r.id] ? ` · ${payInfo[r.id].type || r.type || "ADU"}` : "")}{r.designer ? ` · ${zh ? "设计" : "Dsgn"}: ${r.designer}` : ""}</span>
                      </button>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap font-semibold" style={ink(branchColor(r.branch, r.branchLabel))}>
                      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: branchColor(r.branch, r.branchLabel) }} />{r.branchLabel || "-"}</span>
                    </td>
                    <td className="px-3 py-3 text-slate-600 whitespace-nowrap"><span>{r.sales || "-"}</span><span className="text-[11px] text-slate-400 block">{r.teamName || "-"}</span></td>
                    <td className="px-3 py-3"><span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 whitespace-nowrap">{r.source === "signed_customer" ? (zh ? "设计阶段" : "Design") : (r.phase || "-")}</span></td>
                    <td className="px-3 py-3 text-right text-slate-700">{money(r.contract)}</td>
                    <td className="px-3 py-3 text-right text-green-600" data-testid={`pfl-collected-${r.id}`}>{money(r.collected)}{r.otherCollected > 0 && <span className="block text-[10px] text-cyan-700 whitespace-nowrap" title={zh ? "增项及其他已收款（设计费 / 代收 / 未关联变更单的增项）" : "Extras & other receipts"}>{zh ? "总到账" : "total"} {money(r.totalCollected)}</span>}</td>
                    <td className={`px-3 py-3 text-right font-semibold ${r.ar > 0 ? "text-red-600" : "text-slate-300"}`}>{r.ar > 0 ? money(r.ar) : "—"}</td>
                    <td className="px-3 py-3 text-right whitespace-nowrap" data-testid={`pfl-ardays-${r.id}`}>
                      {r.arDays > 0 ? <span className={`text-xs ${arDaysCls(r.arDays)}`}>{r.arDays}{zh ? "天" : "d"}</span> : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap" data-testid={`pfl-paystate-${r.id}`}>
                      {r.source === "signed_customer" ? (
                        <button type="button" data-testid={`pfl-signed-customer-payments-${r.id}`} onClick={() => nav(`/crm/signed/${r.leadId}`)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-left text-[11px] text-brand-600 hover:underline whitespace-nowrap">
                          {r.payPending || r.payInvoiced
                            ? [r.payPending ? `${zh ? "待开单" : "Pending invoice"} ×${r.payPending}` : "", r.payInvoiced ? `${zh ? "已开票" : "Invoiced"} ×${r.payInvoiced}` : ""].filter(Boolean).join(" · ")
                            : (zh ? "查看客户付款" : "View customer payments")}
                        </button>
                      ) : payInfo[r.id]
                        ? <ReceivablePopover project={payInfo[r.id]} api={api} zh={zh} lang={lang} onChanged={reload} />
                        : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td className={`px-3 py-3 text-right font-semibold ${r.ap > 0 ? "text-amber-600" : "text-slate-300"}`}>{r.ap > 0 ? money(r.ap) : "—"}</td>
                    <td className="px-3 py-3 text-right"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${marginCls(r.margin)}`}>{r.margin == null ? "—" : `${r.margin}%`}</span></td>
                    <td className="px-4 py-3 text-center"><span data-testid={`pfl-health-${r.id}`} className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap ${h.cls}`}>{zh ? h.zh : h.en}</span>
                      {r.receiptVarianceFlag && <span className="block mt-0.5 text-[10px] font-semibold text-red-600 whitespace-nowrap" data-testid={`pfl-variance-${r.id}`} title={zh ? "QB 实收与合同收款计划差异超过 10%" : "Receipts differ from schedule > 10%"}>⚑ {zh ? "对账差异" : "variance"} {r.receiptVariancePct >= 0 ? "+" : ""}{r.receiptVariancePct}%</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </ResponsiveTable>
        </div>
        {/* 分页器（服务端分页，每页 20 条） */}
        {list.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-t border-slate-100 text-[11px] text-slate-500" data-testid="pfl-pager">
            <span data-testid="pfl-pager-info" data-page={page} data-pages={pageCount} data-total={total}>
              {zh ? `第 ${page} / ${pageCount} 页 · 共 ${total} 条` : `Page ${page} / ${pageCount} · ${total} rows`}
            </span>
            <div className="flex-wrap md:flex-nowrap flex items-center gap-1">
              <button type="button" data-testid="pfl-pager-first" disabled={page <= 1} onClick={() => setPage(1)}
                className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 px-2 py-1 rounded-md border border-slate-200 bg-white hover:border-brand-400 disabled:opacity-40 disabled:hover:border-slate-200">{zh ? "首页" : "First"}</button>
              <button type="button" data-testid="pfl-pager-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 px-2 py-1 rounded-md border border-slate-200 bg-white hover:border-brand-400 disabled:opacity-40 disabled:hover:border-slate-200">{zh ? "上一页" : "Prev"}</button>
              {pageNums[0] > 1 && <span className="px-1">…</span>}
              {pageNums.map((n) => (
                <button key={n} type="button" data-testid={`pfl-pager-page-${n}`} onClick={() => setPage(n)}
                  className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 min-w-[26px] px-2 py-1 rounded-md border transition-colors ${n === page ? "border-brand-500 bg-brand-50 text-brand-700 font-semibold" : "border-slate-200 bg-white hover:border-brand-400"}`}>{n}</button>
              ))}
              {pageNums[pageNums.length - 1] < pageCount && <span className="px-1">…</span>}
              <button type="button" data-testid="pfl-pager-next" disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 px-2 py-1 rounded-md border border-slate-200 bg-white hover:border-brand-400 disabled:opacity-40 disabled:hover:border-slate-200">{zh ? "下一页" : "Next"}</button>
              <button type="button" data-testid="pfl-pager-last" disabled={page >= pageCount} onClick={() => setPage(pageCount)}
                className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 px-2 py-1 rounded-md border border-slate-200 bg-white hover:border-brand-400 disabled:opacity-40 disabled:hover:border-slate-200">{zh ? "末页" : "Last"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CompareChart({ chart, zh, nav, api }) {
  const [metric, setMetric] = useState("profit");
  const [drill, setDrill] = useState(null);
  const data = useMemo(() => ((chart || {})[metric] || []).map((r) => ({ ...r, short: (r.address || "").split(",")[0].slice(0, 24) })), [chart, metric]);
  if (data.length === 0) return null;
  const dk = metric === "margin" ? "margin" : "profit";
  const title = zh
    ? (metric === "profit" ? `项目盈利对比 · 毛利额 Top ${data.length}` : metric === "margin" ? `项目盈利对比 · 毛利率 Top ${data.length}` : `低盈利/亏损预警 · Bottom ${data.length}`)
    : (metric === "profit" ? `Profit Top ${data.length}` : metric === "margin" ? `Margin% Top ${data.length}` : `Low-profit Bottom ${data.length}`);
  const btn = (key, label, activeCls) => (
    <button data-testid={`pfl-compare-metric-${key}`} onClick={() => { setMetric(key); setDrill(null); }}
      className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 px-3 py-1 rounded-md text-xs font-semibold transition-colors ${metric === key ? `bg-white shadow-sm ${activeCls}` : "text-slate-500"}`}>{label}</button>
  );
  const openDrill = async (entry) => {
    setDrill({ loading: true, id: entry.id });
    try {
      const { data } = await api.get(`/finance/project-profit/${entry.id}`);
      setDrill({ id: entry.id, data });
    } catch (e) { setDrill(null); toast.error(e?.detailText || (zh ? "加载成本明细失败" : "Load cost detail failed")); }
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="pfl-compare-chart">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <p className="text-[11px] text-slate-400">{metric === "low"
            ? (zh ? "点击柱子直接展开该项目成本明细，定位亏损来源" : "Click a bar to drill into cost breakdown")
            : (zh ? "毛利 = 已收 − 成本支出；条末标注毛利率。点击柱子进入该项目财务详情" : "Profit = collected − cost; margin% labeled. Click a bar to open detail")}</p>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          {btn("profit", zh ? "毛利额" : "Profit $", "text-green-600")}
          {btn("margin", zh ? "毛利率" : "Margin %", "text-blue-600")}
          {btn("low", zh ? "亏损预警" : "Low profit", "text-red-600")}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={Math.max(220, data.length * 28 + 40)}>
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 56, left: 8, bottom: 0 }}>
          <XAxis type="number" tickFormatter={(v) => metric === "margin" ? `${v}%` : "$" + (Math.abs(v) >= 1000 ? Math.round(v / 1000) + "k" : v)} tick={{ fontSize: 11, fill: "#64748b" }} />
          <YAxis type="category" dataKey="short" width={180} tick={{ fontSize: 11, fill: "#334155" }} />
          <Tooltip formatter={(v, n, p) => {
            const r = p?.payload || {};
            return [metric === "margin" ? `${v}%` : money(v), (zh ? "毛利" : "Profit") + (metric === "margin" ? "率" : "") + ` · ${zh ? "毛利率" : "margin"} ${r.margin != null ? r.margin + "%" : "—"} · ${zh ? "合同" : "contract"} ${money(r.contract)}`];
          }} labelFormatter={(l, p) => p?.[0]?.payload?.address || l} />
          <Bar dataKey={dk} radius={[0, 5, 5, 0]} cursor="pointer" isAnimationActive={false}
            onClick={(entry) => { if (!entry?.id) return; if (metric === "low") openDrill(entry); else nav(`/finance/project/${entry.id}`); }}>
            {data.map((r) => <Cell key={r.id} fill={metric === "low" ? ((r.profit || 0) < 0 ? "#dc2626" : "#f59e0b") : branchColor(r.branch, r.branchLabel)} />)}
            <LabelList dataKey="margin" position="right" formatter={(v) => (v == null ? "" : `${v}%`)} style={{ fontSize: 10, fill: "#64748b" }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {metric === "low" && drill && <LossDrill drill={drill} zh={zh} nav={nav} onClose={() => setDrill(null)} />}
    </div>
  );
}

const CAT_ZH = { material: "材料", labor: "人工", subcontractor: "分包", permit: "许可", equipment: "设备",
  site_facilities: "现场设施", logistics: "物流", waste_disposal: "垃圾清运", project_insurance: "项目保险",
  testing: "检测", bonds: "保证金", design_eng: "设计与工程", other: "其他" };

function LossDrill({ drill, zh, nav, onClose }) {
  if (drill.loading) return <div className="mt-4 rounded-xl border border-slate-100 p-6 text-center text-sm text-slate-400" data-testid="pfl-loss-drill-loading">{zh ? "加载成本明细…" : "Loading…"}</div>;
  const d = drill.data;
  const cats = Object.entries(d.byCategory || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  const items = [
    ...(d.bills || []).map((b) => ({ name: b.vendorName || b.name || b.title || (zh ? "账单" : "Bill"), cat: b.category, amount: Number(b.amount || 0) })),
    ...(d.expenses || []).map((e) => ({ name: e.description || e.name || e.title || (zh ? "支出" : "Expense"), cat: e.category, amount: Number(e.amount || 0) })),
  ].sort((a, b) => b.amount - a.amount).slice(0, 8);
  const maxCat = cats.length ? cats[0][1] : 1;
  const chip = (label, val, cls) => (
    <div className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${cls}`}>{label}: {money(val)}</div>
  );
  return (
    <div className="mt-4 rounded-xl border border-red-100 bg-red-50/40 p-4" data-testid="pfl-loss-drill">
      <div className="flex-wrap md:flex-nowrap flex items-start justify-between gap-2 mb-3">
        <div>
          <p className="font-semibold text-slate-800 text-sm">{d.address} <span className="text-slate-400 font-normal">· {d.clientName || "-"}</span></p>
          <p className="text-[11px] text-slate-400">{zh ? "成本明细下钻 · 找出亏损来源" : "Cost breakdown drill-down"}</p>
        </div>
        <div className="flex-wrap md:flex-nowrap flex items-center gap-2 shrink-0">
          <button data-testid="pfl-drill-detail" onClick={() => nav(`/finance/project/${d.projectId}`)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 underline">{zh ? "完整财务详情 →" : "Full detail →"}</button>
          <button data-testid="pfl-drill-close" onClick={onClose} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-400 hover:text-slate-600 text-sm font-bold px-1">✕</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {chip(zh ? "合同额" : "Contract", d.revenue, "bg-slate-100 text-slate-600")}
        {chip(zh ? "已收" : "Collected", d.collected, "bg-emerald-100 text-emerald-700")}
        {chip(zh ? "总成本" : "Cost", d.cost, "bg-amber-100 text-amber-700")}
        {chip(zh ? "毛利" : "Profit", d.grossProfit, d.grossProfit < 0 ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700")}
        <div className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${d.margin < 0 ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>{zh ? "毛利率" : "Margin"}: {d.margin}%</div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div data-testid="pfl-drill-cats">
          <p className="text-[11px] font-semibold text-slate-500 mb-1.5">{zh ? "成本构成（按类别）" : "Cost by category"}</p>
          {cats.length === 0 && <p className="text-xs text-slate-300">{zh ? "暂无成本记录" : "No cost records"}</p>}
          {cats.map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 mb-1">
              <span className="text-[11px] text-slate-500 w-16 shrink-0">{zh ? (CAT_ZH[k] || k) : k}</span>
              <div className="flex-1 h-3 rounded bg-white overflow-hidden"><div className="h-full rounded bg-red-400/80" style={{ width: `${Math.max(4, (v / maxCat) * 100)}%` }} /></div>
              <span className="text-[11px] font-semibold text-slate-600 w-16 text-right">{money(v)}</span>
              <span className="text-[10px] text-slate-400 w-10 text-right">{d.cost ? Math.round((v / d.cost) * 100) : 0}%</span>
            </div>
          ))}
        </div>
        <div data-testid="pfl-drill-items">
          <p className="text-[11px] font-semibold text-slate-500 mb-1.5">{zh ? "最大成本项 Top 8" : "Top cost items"}</p>
          {items.length === 0 && <p className="text-xs text-slate-300">{zh ? "暂无成本记录" : "No items"}</p>}
          {items.map((it, i) => (
            <div key={i} className="flex-wrap md:flex-nowrap flex items-center justify-between text-xs border-b border-red-100/60 py-1">
              <span className="text-slate-600 truncate mr-2">{it.name}<span className="ml-1.5 text-[10px] text-slate-400">{zh ? (CAT_ZH[it.cat] || it.cat || "") : (it.cat || "")}</span></span>
              <span className="font-semibold text-red-600 shrink-0">{money(it.amount)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, icon: Icon, color, tid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3.5" data-testid={tid}>
      <div className="flex-wrap md:flex-nowrap flex items-center justify-between">
        <p className="text-[11px] font-medium text-slate-500">{label}</p>
        <Icon className="h-4 w-4" style={ink(color)} />
      </div>
      <p className="text-lg font-extrabold mt-1 text-slate-900">{value}</p>
      {sub && <p className="text-[10px] font-semibold mt-0.5" style={ink(color)}>{sub}</p>}
    </div>
  );
}
